import { ProductBadges } from "@components/ui/product-badges/ProductBadges";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Grid } from "@ecco/ui/grid";
import { Collapse } from "@ecco/ui/collapse";
import { chakraRef } from "@ecco/ui/forward-ref";
import { getImageAltTag } from "@ecco/ui/product-name-transform";
import dynamic from "next/dynamic";
import type { Dispatch, SetStateAction } from "react";
import { space } from "@ecco/ui/tokens";
import type { Product, ProductTileProps } from "../ProductTile";
import { ProductImageFrame } from "@ecco/ui/product-image-frame";
import { getValidSaleBadge } from "@lib/utils/pdp/saleBadgeUtils";
import type { EccoClubBadge } from "@lib/wrappers/algolia/types";
import type { SwiperClass } from "@ecco/ui/swiper-exports";

const ProductTileImageCarousel = dynamic(() =>
    import("./ProductTileImageCarousel").then((mod) => mod.ProductTileImageCarousel)
);

interface ProductImagesProps {
    initialProduct: Product;
    displayedProduct: Product;
    productColors: ProductTileProps["productColors"];
    productName: ProductTileProps["productName"];
    setDisplayedProduct: Dispatch<SetStateAction<Product>>;
    isExpanded: boolean;
    onOpen: () => void;
    onClose: () => void;
    altTagPattern?: string;
    discountPercentage: number;
    customBadges?: string[];
    eccoClubBadge?: EccoClubBadge;
    hasCompactBadgesOnDesktop?: boolean;
    images?: string[];
    onCarouselSwiper?: (swiper: SwiperClass) => void;
}

export const ProductImages = chakraRef(
    (
        {
            setDisplayedProduct,
            displayedProduct,
            initialProduct,
            productColors,
            isExpanded,
            onOpen,
            onClose,
            altTagPattern,
            discountPercentage,
            customBadges,
            eccoClubBadge,
            hasCompactBadgesOnDesktop,
            images,
            onCarouselSwiper,
        }: ProductImagesProps,
        ref
    ) => {
        const setCurrentProduct = (item: Product) => {
            setDisplayedProduct({
                ...displayedProduct,
                ...item,
            });
        };

        const setInitialProduct = () => {
            setDisplayedProduct(initialProduct);
        };

        const productColorsWithStock = productColors?.filter((item) => item.hasStock) ?? [];
        const additionalColors = `+${Math.max(productColorsWithStock.length - 5, 0)}`;
        const saleBadge = getValidSaleBadge(displayedProduct.eaBadge);

        // Variant hover swaps the image to a single variant; the carousel only runs
        // for the initial product when it has more than one image.
        const isPreviewingVariant = displayedProduct.objectID !== initialProduct.objectID;
        const carouselImages = !isPreviewingVariant && images && images.length > 1 ? images : null;

        const mainImageAlt = getImageAltTag(
            displayedProduct?.altTagPattern ?? altTagPattern,
            displayedProduct?.imageLabel
        );

        return (
            <>
                {/* Main Product Image */}
                <Box
                    onMouseEnter={onOpen}
                    onMouseLeave={onClose}
                    aspectRatio="4/5"
                    position="relative"
                    width="100%"
                >
                    {carouselImages ? (
                        <ProductTileImageCarousel
                            images={carouselImages}
                            isExpanded={isExpanded}
                            alt={mainImageAlt}
                            onSwiperInit={onCarouselSwiper}
                        />
                    ) : (
                        <ProductImageFrame
                            sizes={["50vw", "50vw", "33vw", "25vw"]}
                            src={displayedProduct?.image}
                            alt={mainImageAlt}
                        />
                    )}
                    {(discountPercentage > 0 ||
                        customBadges?.length > 0 ||
                        displayedProduct?.eaBadge ||
                        eccoClubBadge) && (
                        <ProductBadges
                            discount={
                                discountPercentage > 0 ? `-${discountPercentage}%` : undefined
                            }
                            customBadges={customBadges ?? []}
                            saleBadge={saleBadge}
                            eccoClubBadge={eccoClubBadge}
                            hasCompactBadgesOnDesktop={hasCompactBadgesOnDesktop}
                        />
                    )}
                </Box>

                {/* Color Swatches */}
                <Box
                    display={["none", "block"]}
                    pl={[4, 4, 4, 6]}
                    w="full"
                    onMouseEnter={onOpen}
                    onMouseLeave={onClose}
                    ref={ref}
                    pt={isExpanded ? 3 : 0}
                    transition="padding-top 0.3s"
                >
                    <Collapse
                        transition={{ enter: { duration: 0.3 }, exit: { duration: 0.3 } }}
                        in={isExpanded}
                        animateOpacity
                        startingHeight={0.01}
                    >
                        <Grid
                            data-testid="product-image-swatches"
                            templateColumns="repeat(auto-fit, minmax(15%, 15%))"
                            h="full"
                            gap={space["0.2"]}
                        >
                            {[initialProduct, ...productColorsWithStock.slice(0, 5)].map(
                                (item, index) => (
                                    <Box
                                        key={item.objectID}
                                        gridColumn={index + 1}
                                        onMouseEnter={() => setCurrentProduct(item)}
                                        onMouseLeave={setInitialProduct}
                                        aspectRatio="4/5"
                                        display="flex"
                                        alignItems="flex-end"
                                        width="100%"
                                    >
                                        <ProductImageFrame
                                            // Thumbnail images don't need to be loaded in high quality.
                                            sizes={["10vw", "10vw", "5vw", "5vw"]}
                                            src={item?.image || ""}
                                            alt={
                                                getImageAltTag(
                                                    item?.altTagPattern ?? altTagPattern,
                                                    item?.imageLabel
                                                ) || ""
                                            }
                                        />
                                    </Box>
                                )
                            )}
                            {productColorsWithStock.length > 5 && (
                                <Flex gridColumn={7} justifyContent="center" alignItems="center">
                                    <Text size="large" fontWeight="bold" color="black">
                                        {additionalColors}
                                    </Text>
                                </Flex>
                            )}
                        </Grid>
                    </Collapse>
                </Box>
            </>
        );
    }
);

ProductImages.displayName = "ProductImages";
